// Client-side image resize. Pre-upload, scales each file down to a
// bounding box (fit-inside, never upscale by default) via
// `createImageBitmap` + `OffscreenCanvas.convertToBlob`. Strips EXIF as
// a side effect of the canvas path; honors EXIF orientation via
// `imageOrientation: 'from-image'` so portrait DSLR shots render upright
// in the email.
//
// **Output format.** Source MIME preserved. PNG with alpha keeps the
// alpha (canvas → PNG retains transparency). JPEG / WebP use the
// passed `quality` (default 0.85 — D-121); PNG ignores it.
//
// **Fallback.** Safari ≤14 + corporate-locked browsers may lack
// `createImageBitmap` or `OffscreenCanvas.convertToBlob`. On
// `isResizeSupported() === false` callers fall back to uploading the
// original File. We do not throw a user-facing error.
//
// **Modes** (P-037.29 post-Phase-Close §item C → revised at §item G):
//
// - `'inside'` (default): fit-inside. Preserves source aspect, never
//   upscales. Source ends up ≤ target on both axes. Used by IMG
//   consumers whose target dims happen to match source aspect, or
//   where preserving the WHOLE source is more important than filling
//   the target rect (e.g., transparent-PNG signatures).
//
// - `'cover'` (post-Phase-Close §item G — supersedes the short-lived
//   `'outside'` mode shipped at §item C): cover-crop at upload time.
//   Center-crops the source to the target aspect, then scales the
//   crop to exact target dims (or as close as never-upscale allows).
//   Source bytes come out exactly target dims when source ≥ target on
//   both axes; never upscaled otherwise. Used by IMG consumers that
//   would otherwise rely on the browser to stretch the source — the
//   stretch distorts aspect AND softens because IMG width/height
//   attributes behave like CSS `object-fit: fill`. Cover-crop at
//   upload bakes the right aspect into the bytes so every email
//   client renders without distortion.
//
//   Replaces `'outside'` (fit-outside, no crop) — the two composite
//   logo consumers that used `'outside'` got cover-crop instead. The
//   composite Canvas still calls `coverSourceRect` internally; for
//   pre-cropped 1:1 logos that's a no-op (same output, cleaner
//   upload bytes).
//
// **`bakeInsetShadow`** (post-Phase-Close §item G): when set on the
// target, apply the shared `applyInsetShadow` from `./image-shadow.ts`
// to the resized canvas before encoding. Bakes a soft inset shadow
// ring into the image bytes so the visual effect renders in every
// email client (Outlook desktop silently drops CSS `box-shadow:
// inset`). Used by direct-render IMG fields (EventCard, SponsorCard,
// `images` Block). Composite-source fields rely on the composite
// pipeline's own internal `applyInsetShadow` call — they do NOT set
// this flag (would double up).

import type { ImageTarget } from './image-dimensions'
import { applyInsetShadow, DEFAULT_INSET_SHADOW_BLUR_PX } from './image-shadow'

export interface ResizeOptions extends ImageTarget {
  readonly mimeType?: string
  readonly quality?: number
}

export interface ResizeResult {
  readonly blob: Blob
  readonly width: number
  readonly height: number
}

/**
 * P-037.31 §B — min-size validation result. `ok` is true iff source ≥
 * target on BOTH axes; `srcWidth` / `srcHeight` are surfaced so callers
 * can interpolate the actual dims into a user-facing rejection toast.
 */
export interface ValidateImageSizeResult {
  readonly ok: boolean
  readonly srcWidth: number
  readonly srcHeight: number
}

const DEFAULT_QUALITY = 0.85

/**
 * Returns true when the browser supports the resize pipeline. False on
 * Safari ≤14 and any environment missing `createImageBitmap` or
 * `OffscreenCanvas.convertToBlob`. Callers use this to fall back to
 * uploading the original File without raising a user-facing error.
 */
export function isResizeSupported(): boolean {
  if (typeof createImageBitmap !== 'function') return false
  if (typeof OffscreenCanvas !== 'function') return false
  // convertToBlob lands later than the canvas itself on Safari; spot-
  // check the prototype rather than instantiating a canvas.
  return typeof OffscreenCanvas.prototype.convertToBlob === 'function'
}

/**
 * P-037.31 §B — measure a source File/Blob's natural dimensions via
 * `createImageBitmap` and compare to a target box. `ok` is true iff
 * source ≥ target on BOTH axes. Callers thread the registry's `@2x
 * retina target` (`toRetinaTarget(getImageTarget(...))`) — the
 * threshold matches what the resize pipeline would upload at.
 *
 * Throws if `createImageBitmap` is unavailable (Safari ≤14 + locked-
 * down browsers). Callers should `try`/`catch` and degrade silently
 * (let the upload proceed unguarded) — same fallback posture as the
 * resize pipeline at D-121, so the field's behavior stays consistent
 * across environments.
 *
 * Closes the bitmap in `finally` to release the underlying decoded-
 * pixel buffer.
 */
export async function validateImageSize(
  source: File | Blob,
  target: ImageTarget
): Promise<ValidateImageSizeResult> {
  const bitmap = await createImageBitmap(source)
  try {
    const srcWidth = bitmap.width
    const srcHeight = bitmap.height
    const ok = srcWidth >= target.width && srcHeight >= target.height
    return { ok, srcWidth, srcHeight }
  } finally {
    bitmap.close()
  }
}

/**
 * P-037.31 §B — shared rejection message for both upload paths
 * (file-picker arm in `<UploadModal>`, paste-URL arm in
 * `<ImageUrlField>`). Format matches Banks's brief: target W × H first,
 * actual source W × H in parens, friendly "blurry on retina" rationale.
 * Lives here (not in image-url-field) so the upload-modal can import
 * without creating a module-graph cycle through the static
 * `import type { UploadModal } from './upload-modal'` in image-url-field.
 */
export function tooSmallMessage(
  srcWidth: number,
  srcHeight: number,
  target: ImageTarget
): string {
  return (
    `Image must be at least ${target.width} × ${target.height} pixels ` +
    `(uploaded: ${srcWidth} × ${srcHeight}). ` +
    `Smaller images would print blurry on retina displays.`
  )
}

/**
 * Resize `file` to fit the target dimensions per `opts.mode`. Returns
 * the resized Blob plus its actual dimensions. Never upscales — when
 * the source is too small for the target, the output dimensions stay
 * at source-derived sizes.
 *
 * Throws if `isResizeSupported() === false`. Callers guard via
 * `isResizeSupported()` before calling.
 */
export async function resizeImage(file: File, opts: ResizeOptions): Promise<ResizeResult> {
  const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
  try {
    const plan = opts.mode === 'cover' ? planCover(bitmap, opts) : planInside(bitmap, opts)
    const canvas = new OffscreenCanvas(plan.outW, plan.outH)
    const ctx = canvas.getContext('2d')
    if (ctx === null) {
      throw new Error('Canvas 2D context unavailable.')
    }
    ctx.drawImage(bitmap, plan.srcX, plan.srcY, plan.srcW, plan.srcH, 0, 0, plan.outW, plan.outH)
    // Post-Phase-Close §item G — bake inset shadow into the bytes when
    // the target opts in. Same algorithm + opacity as the composite
    // pipeline's call (shared via `./image-shadow.ts`).
    if (opts.bakeInsetShadow === true) {
      applyInsetShadow(ctx, 0, 0, plan.outW, plan.outH, {
        blurPx: DEFAULT_INSET_SHADOW_BLUR_PX,
      })
    }
    // P-048.10 / D-153 — force PNG output for WebP sources. Outlook
    // desktop's Word renderer doesn't support WebP (documented format
    // incompatibility) — uploads stay broken in Outlook unless we
    // re-encode. Banks's Outlook smoke surfaced this on the Lee
    // Cornwell author avatar (uploaded as WebP, rendered as a broken-
    // image icon in Outlook). PNG preserves alpha + is supported by
    // every email client; file sizes grow ~3-5× vs WebP but the
    // tradeoff is mandatory for Outlook compatibility. Other source
    // MIME types (JPG, PNG, GIF) are already Outlook-compatible and
    // pass through unchanged. Explicit `opts.mimeType` override still
    // wins for future callers that need it (e.g., compositor that
    // already encodes to PNG).
    const sourceMime = opts.mimeType ?? file.type
    const mimeType = sourceMime === 'image/webp' ? 'image/png' : sourceMime
    const blob = await canvas.convertToBlob({
      type: mimeType,
      quality: opts.quality ?? DEFAULT_QUALITY,
    })
    return { blob, width: plan.outW, height: plan.outH }
  } finally {
    bitmap.close()
  }
}

interface DrawPlan {
  readonly srcX: number
  readonly srcY: number
  readonly srcW: number
  readonly srcH: number
  readonly outW: number
  readonly outH: number
}

/**
 * Fit-inside plan: full source, scaled to fit inside (maxW, maxH).
 * Never upscales. Source aspect preserved.
 */
function planInside(bitmap: ImageBitmap, opts: ResizeOptions): DrawPlan {
  const srcW = bitmap.width
  const srcH = bitmap.height
  if (srcW <= opts.width && srcH <= opts.height) {
    return { srcX: 0, srcY: 0, srcW, srcH, outW: srcW, outH: srcH }
  }
  const scale = Math.min(opts.width / srcW, opts.height / srcH)
  return {
    srcX: 0,
    srcY: 0,
    srcW,
    srcH,
    outW: Math.max(1, Math.round(srcW * scale)),
    outH: Math.max(1, Math.round(srcH * scale)),
  }
}

/**
 * Cover plan (post-Phase-Close §item G): center-crop the source to the
 * target aspect (maxW : maxH), then scale the crop to exact target
 * dims (clamped to ≤ source crop dims — never upscales).
 *
 * Three regimes:
 *
 *   1. Source ≥ target on both axes — the most common case for staff
 *      photos at modern phone-camera resolutions. Crop rect at target
 *      aspect filled from the center of source; the crop's dims (sw,
 *      sh) ≥ (maxW, maxH); scale = maxW/sw = maxH/sh; output exactly
 *      (maxW, maxH). No distortion, no upscale, exact target bytes.
 *
 *   2. Source < target on one or both axes — rare; chapter convention
 *      requires sources ≥ target @2x. Crop rect at target aspect is
 *      still center-cropped from source, but it's smaller than the
 *      target box on the limiting axis. We never-upscale-clamp the
 *      scale to 1.0, so output dims = crop dims (smaller than target).
 *      The IMG tag will then stretch the source uniformly in the
 *      browser — degraded but at least correct aspect.
 *
 *   3. Source aspect == target aspect (edge case of 1) — no cropping
 *      needed; crop rect == full source; scale to target.
 *
 * Output dims are exact: ≤ target box on both axes; aspect matches
 * target.
 */
function planCover(bitmap: ImageBitmap, opts: ResizeOptions): DrawPlan {
  const srcW = bitmap.width
  const srcH = bitmap.height
  const targetAspect = opts.width / opts.height
  const srcAspect = srcW / srcH
  // 1. Compute the cover-crop rect inside source (centered, target
  //    aspect, largest possible).
  let cropX: number
  let cropY: number
  let cropW: number
  let cropH: number
  if (srcAspect > targetAspect) {
    // Source is wider than target — crop horizontally (keep full
    // height, narrow the width to match target aspect).
    cropW = srcH * targetAspect
    cropH = srcH
    cropX = (srcW - cropW) / 2
    cropY = 0
  } else {
    // Source is taller-or-equal — crop vertically (keep full width,
    // shorten height to match target aspect).
    cropW = srcW
    cropH = srcW / targetAspect
    cropX = 0
    cropY = (srcH - cropH) / 2
  }
  // 2. Determine output scale. cropW/cropH already match target
  //    aspect, so either axis gives the same ratio — pick width. Clamp
  //    to ≤ 1.0 (never upscale at upload time; the IMG will stretch
  //    when the crop is smaller than the target box — acceptable
  //    degradation over uniform upscale here).
  const scale = Math.min(1, opts.width / cropW)
  return {
    srcX: cropX,
    srcY: cropY,
    srcW: cropW,
    srcH: cropH,
    outW: Math.max(1, Math.round(cropW * scale)),
    outH: Math.max(1, Math.round(cropH * scale)),
  }
}
